import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField, Label } from "semantic-ui-react";

import { ReactTable } from "components";
import { createFormHelper } from "lib/Form";
import { SEASONS } from "models";
import { useSearchCompleteTyresQuery } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/CompleteTyresSearch/hooks";
import { getTyreSeasonName } from "modules/IncompleteTyresDashboard/util";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { Tyre } from "models";
import type { SearchCompleteTyresPayload } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/CompleteTyresSearch/hooks";
import type { EditTyreFormValues } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/EditTyreForm";

type CompleteTyresSearchProps = {
  onSelectReplacement: (tyre: Tyre) => void;
};

const FormHelper = createFormHelper<EditTyreFormValues>();

const TRIGGER_FIELDS = ["manufacturer", "width", "size", "speed", "height", "season"];
const RESULTS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const buildSearchPayload = (values: EditTyreFormValues): SearchCompleteTyresPayload => ({
  ...values,
  size: parseFloat(values.size) || 0,
  season: values.season === SEASONS.NoSeason ? null : values.season
});

export const CompleteTyresSearch = ({ onSelectReplacement }: CompleteTyresSearchProps) => {
  const { t } = useTranslation();
  const { watch, getValues } = FormHelper.useFormContext();

  const [searchPayload, setSearchPayload] = useState(() => buildSearchPayload(getValues()));
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: RESULTS_PAGE_SIZE });

  const debouncedUpdatePayload = useMemo(
    () =>
      debounce(() => {
        const payload = buildSearchPayload(getValues());
        setSearchPayload(currentPayload => (isEqual(currentPayload, payload) ? currentPayload : payload));
      }, SEARCH_DEBOUNCE_MS),
    [getValues]
  );

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name && TRIGGER_FIELDS.includes(name)) debouncedUpdatePayload();
    });

    return () => {
      subscription.unsubscribe();
      debouncedUpdatePayload.cancel();
    };
  }, [watch, debouncedUpdatePayload]);

  const { data: results, isFetching } = useSearchCompleteTyresQuery(searchPayload);

  const columns = useMemo<AccessorColumnDef<Tyre>[]>(
    () => [
      {
        id: "manufacturer",
        size: 150,
        accessorKey: "manufacturer",
        header: () => <span className="no-wrap-text">{t("v8_manufacturer", "Manufacturer")}</span>,
        cell: ({ row: { original } }) => original.manufacturer
      },
      {
        id: "width",
        size: 50,
        accessorKey: "width",
        header: () => <span className="no-wrap-text">{t("v8_width", "Width")}</span>,
        cell: ({ row: { original } }) => original.width
      },
      {
        id: "size",
        size: 50,
        accessorKey: "size",
        header: () => <span className="no-wrap-text">{t("v8_size", "Size")}</span>,
        cell: ({ row: { original } }) => original.size
      },
      {
        id: "speed",
        size: 50,
        accessorKey: "speed",
        header: () => <span className="no-wrap-text">{t("v8_speed", "Speed")}</span>,
        cell: ({ row: { original } }) => original.speed
      },
      {
        id: "height",
        size: 50,
        accessorKey: "height",
        header: () => <span className="no-wrap-text">{t("v8_height", "Height")}</span>,
        cell: ({ row: { original } }) => original.height
      },
      {
        id: "season",
        size: 100,
        accessorKey: "season",
        header: () => <span className="no-wrap-text">{t("v8_season", "Season")}</span>,
        cell: ({ row: { original } }) => getTyreSeasonName(original.season, t)
      },
      {
        id: "description",
        size: 250,
        accessorKey: "description",
        header: () => <span className="no-wrap-text">{t("v8_description", "Description")}</span>,
        cell: ({ row: { original } }) => original.description
      },
      {
        id: "replace",
        size: 80,
        accessorFn: row => row,
        header: () => null,
        cell: ({ row: { original } }) => (
          <Label color="green" className="replace-label" onClick={() => onSelectReplacement(original)}>
            {t("v8_replace", "Replace")}
          </Label>
        )
      }
    ],
    [t, onSelectReplacement]
  );

  const totalItems = results?.length ?? 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);
  const pagedResults = useMemo(
    () => (results ?? []).slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize),
    [results, pagination]
  );

  return (
    <div className="CompleteTyresSearch">
      <FormField>
        <label>{t("v8_list_of_complete_tyres", "List of complete tyres")}</label>
      </FormField>
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={pagedResults}
        pagination={pageCount > 1 ? pagination : undefined}
        pageCount={pageCount}
        totalItems={totalItems}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
      />
    </div>
  );
};
